import React, {useState, useEffect} from 'react';
import {useNavigate, useLocation} from 'react-router-dom';
import {Building2, ArrowRight, Loader2} from 'lucide-react';

import {useQuery} from '@apollo/client/react';
import Api from '../Api';
import {gql} from 'types/__generated__';

// Simple query to check if user is logged in
const CHECK_AUTH_QUERY = gql(`
  query CheckAuth {
    loginInfo {
      user {
        username
      }
    }
  }
`);

interface OrganisationMembership {
  organisationId: string;
  name: string;
}

export default function SelectOrganisationPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedOrg, setSelectedOrg] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [organisations, setOrganisations] = useState<OrganisationMembership[]>([]);

  // Check if user is already authenticated
  const {data: authData, loading: authLoading} = useQuery(CHECK_AUTH_QUERY, {
    fetchPolicy: 'network-only',
  });

  // Get organizations from location state (passed from login)
  useEffect(() => {
    // If we're still checking auth, wait
    if (authLoading) return;

    const orgs = location.state?.organisations || [];
    if (orgs.length === 0) {
      // No organizations provided - either user is logged in or needs to log in
      if (authData?.loginInfo?.user) {
        // User is authenticated, go to dashboard
        console.log('User authenticated, redirecting to dashboard');
        navigate('/admin/dashboard', {replace: true});
      } else {
        // User not authenticated, go to login
        console.log('User not authenticated, redirecting to login');
        navigate('/admin/auth/login-page', {replace: true});
      }
      return;
    }
    setOrganisations(orgs);
  }, [location.state, navigate, authData, authLoading]);

  const handleSelectOrganisation = () => {
    if (!selectedOrg) {
      setError('Please select an organization');
      return;
    }

    setLoading(true);
    setError('');

    Api.selectOrganisation(
      selectedOrg,
      (data: any) => {
        console.log('Organization selected successfully', data);
        setLoading(false);
        // Navigate to dashboard after successful org selection
        navigate('/admin/dashboard', {replace: true});
      },
      (error: any) => {
        console.error('Failed to select organization:', error);
        setError('Failed to select organization. Please try again.');
        setLoading(false);
      },
    );
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-blue-100 dark:from-gray-900 dark:to-gray-800 px-4">
      <div className="max-w-2xl w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto h-16 w-16 bg-gradient-to-br from-blue-500 to-blue-700 rounded-xl flex items-center justify-center shadow-lg">
            <Building2 className="text-white" size={32} />
          </div>
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900 dark:text-white">Select Organization</h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Choose an organization to access the admin dashboard</p>
        </div>

        {/* Organization Selection */}
        <div className="bg-white dark:bg-gray-800 py-8 px-6 shadow-xl rounded-lg border border-gray-200 dark:border-gray-700">
          {error && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-800 dark:text-red-400 text-sm">
              {error}
            </div>
          )}

          <div className="space-y-4">
            {organisations.length === 0 ? (
              <p className="text-center text-gray-500 dark:text-gray-400 py-8">No organizations available</p>
            ) : (
              <>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Available Organizations</label>
                <div className="space-y-2">
                  {organisations.map((org: any) => (
                    <button
                      key={org.organisationId}
                      onClick={() => setSelectedOrg(org.organisationId)}
                      className={`w-full text-left px-4 py-4 rounded-lg border-2 transition-all ${
                        selectedOrg === org.organisationId
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-medium text-gray-900 dark:text-white">{org.name}</h3>
                        </div>
                        {selectedOrg === org.organisationId && (
                          <div className="h-6 w-6 rounded-full bg-blue-600 flex items-center justify-center">
                            <svg className="h-4 w-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>

                <button
                  onClick={handleSelectOrganisation}
                  disabled={!selectedOrg || loading}
                  className="w-full mt-6 flex justify-center items-center gap-2 py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? (
                    <>
                      <Loader2 className="animate-spin" size={20} />
                      Selecting...
                    </>
                  ) : (
                    <>
                      Continue
                      <ArrowRight size={20} />
                    </>
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
